export const SHARED_WITH_ME_FIELDS = 'id,fragrance_id,fragrance_ref,shared_by,received_on,created_at,entity_kind,recipe_id,recipe_ref'

const DISMISSED_PREFIX = 'fos_shared_routing_dismissed_v1:'
const STACK_CACHE_KEY = 'fos_shared_stack_payloads_v1'
const PENDING_STACK_PREFIX = 'fos_shared_stack_pending_v1:'

function storageOrNull(storage) {
  if (storage) return storage
  try { return window.localStorage } catch { return null }
}

function readJson(storage, key, fallback) {
  try { return JSON.parse(storage?.getItem(key) || '') } catch { return fallback }
}

function normalized(value) {
  return String(value || '').trim().toLocaleLowerCase()
}

export function parseFragranceReference(value) {
  const ref = String(value || '').trim()
  const split = ref.toLocaleLowerCase().lastIndexOf(' by ')
  if (split < 1) return { name: ref, house: '' }
  return { name: ref.slice(0, split).trim(), house: ref.slice(split + 4).trim() }
}

export function sharedStackFingerprint(value) {
  return `${normalized(value?.sharer_name || value?.shared_by)}\u0000${normalized(value?.name || value?.recipe_ref)}`
}

// The public share payload deliberately excludes user-typed recipe notes. Cache
// only that same allowlist so a recipient can finish saving the stack later.
export function safeSharedStackPayload(payload) {
  if (payload?.kind !== 'combination' || !payload.name || !payload.sharer_name || !Array.isArray(payload.components)) return null
  return {
    kind: 'combination',
    name: String(payload.name).trim(),
    sharer_name: String(payload.sharer_name).trim(),
    components: payload.components.slice(0, 6).map((row, index) => ({
      position: Number(row.position) || index + 1,
      kind: String(row.kind || 'fragrance'),
      label: String(row.label || '').trim(),
      count: Math.max(1, Math.min(50, Number(row.count) || 1)),
    })).filter(row => row.label),
  }
}

export function cacheSharedStackPayload(payload, storage) {
  const safe = safeSharedStackPayload(payload)
  const target = storageOrNull(storage)
  if (!safe || !target) return safe
  const cache = readJson(target, STACK_CACHE_KEY, {})
  cache[sharedStackFingerprint(safe)] = safe
  try { target.setItem(STACK_CACHE_KEY, JSON.stringify(cache)) } catch { /* Storage is an optional continuity aid. */ }
  return safe
}

export function loadCachedSharedStack(row, storage) {
  const target = storageOrNull(storage)
  const cache = readJson(target, STACK_CACHE_KEY, {})
  return cache[sharedStackFingerprint(row)] || null
}

export function rememberPendingSharedStack(userId, token, payload, storage) {
  const safe = safeSharedStackPayload(payload)
  const target = storageOrNull(storage)
  if (!userId || !token || !safe || !target) return null
  const key = `${PENDING_STACK_PREFIX}${userId}`
  const pending = readJson(target, key, {})
  pending[token] = safe
  try { target.setItem(key, JSON.stringify(pending)) } catch { /* The database record remains the fallback. */ }
  return safe
}

export function dismissedShareIds(userId, storage) {
  const values = readJson(storageOrNull(storage), `${DISMISSED_PREFIX}${userId}`, [])
  return new Set(Array.isArray(values) ? values.map(String) : [])
}

export function dismissIncomingShare(userId, shareId, storage) {
  const target = storageOrNull(storage)
  const values = dismissedShareIds(userId, target)
  values.add(String(shareId))
  try { target?.setItem(`${DISMISSED_PREFIX}${userId}`, JSON.stringify([...values])) } catch { /* Keep the current-session removal in React state. */ }
  return values
}

export async function loadIncomingShareRows(client, userId) {
  if (!userId) return []
  const { data, error } = await client.from('shared_with_me').select(SHARED_WITH_ME_FIELDS)
    .eq('user_id', userId).order('created_at', { ascending: false }).limit(100)
  if (error) throw error
  return data || []
}

async function catalogForReference(client, reference) {
  const parsed = parseFragranceReference(reference)
  if (!parsed.name || !parsed.house) return { ...parsed, catalog: null }
  const { data, error } = await client.from('catalog')
    .select('id,name,house,concentration,card_image,top_notes,mid_notes,base_notes,accord_tags,scent_family,layering_role,best_layer_pair_type')
    .ilike('name', parsed.name).ilike('house', parsed.house).limit(5)
  if (error) return { ...parsed, catalog: null }
  const exact = (data || []).filter(row => normalized(row.name) === normalized(parsed.name) && normalized(row.house) === normalized(parsed.house))
  // A shared card does not carry concentration. Never guess among EDT/EDP/etc.
  // Leaving the row unresolved opens the existing concentration picker.
  const catalog = exact.length === 1 ? exact[0] : null
  return { ...parsed, catalog }
}

export async function hydrateIncomingFragranceShares(client, rows) {
  const incoming = rows.filter(row => (row.entity_kind || 'fragrance') === 'fragrance')
  return Promise.all(incoming.map(async row => {
    const resolved = await catalogForReference(client, row.fragrance_ref)
    return {
      shared_id: row.id,
      is_shared: true,
      shared_by: row.shared_by || 'A FragranceOS member',
      shared_at: row.received_on || row.created_at,
      catalog_id: resolved.catalog?.id || null,
      catalog: resolved.catalog || { name: resolved.name || 'Shared fragrance', house: resolved.house },
    }
  }))
}

export function incomingStackShares(rows, userId, storage) {
  const dismissed = dismissedShareIds(userId, storage)
  const target = storageOrNull(storage)
  const pending = readJson(target, `${PENDING_STACK_PREFIX}${userId}`, {})
  const pendingRows = Object.entries(pending).map(([token, payload]) => ({
    shared_id: `pending:${token}`,
    recipe_id: null,
    name: payload.name,
    shared_by: payload.sharer_name,
    shared_at: null,
    payload,
  }))
  // A recipient record cannot distinguish two source recipes with the same
  // display name. Locally retained token payloads are the exact shares, so they
  // replace matching database summaries and keep same-named shares distinct.
  const pendingFingerprints = new Set(pendingRows.map(sharedStackFingerprint))
  const databaseRows = rows.filter(row => row.entity_kind === 'combination'
    && !dismissed.has(String(row.id))
    && !pendingFingerprints.has(sharedStackFingerprint(row))).map(row => ({
    shared_id: row.id,
    recipe_id: row.recipe_id,
    name: row.recipe_ref || 'Shared stack',
    shared_by: row.shared_by || 'A FragranceOS member',
    shared_at: row.received_on || row.created_at,
    payload: loadCachedSharedStack(row, storage),
  }))
  return [...pendingRows.filter(row => !dismissed.has(String(row.shared_id))), ...databaseRows]
}

export function sharedStackDraft(row, collection) {
  const payload = row?.payload
  if (!payload?.components?.length) return null
  return {
    name: payload.name,
    notes: '',
    source: 'manual',
    shared_id: row.shared_id,
    components: payload.components.map((part, index) => {
      const label = String(part.label || '').trim()
      const exact = part.kind === 'fragrance'
        ? (collection || []).find(item => normalized([item.house, item.name].filter(Boolean).join(' - ')) === normalized(label))
        : null
      return {
        position: index + 1,
        component_kind: part.kind || 'fragrance',
        fragrance_id: exact?.id || '',
        label,
        sprays_or_pumps: part.count,
      }
    }),
  }
}

export async function recordIncomingFragranceShare(client, userId, payload) {
  if (!userId || !payload?.fragrance?.name || !payload?.fragrance?.house || !payload?.sharer_name) return false
  const fragranceRef = `${payload.fragrance.name} by ${payload.fragrance.house}`
  const existing = await client.from('shared_with_me').select('id').eq('user_id', userId)
    .eq('entity_kind', 'fragrance').eq('fragrance_ref', fragranceRef).eq('shared_by', payload.sharer_name).limit(1)
  if (existing.error) throw existing.error
  if (existing.data?.length) return true
  const { error } = await client.from('shared_with_me').insert({
    user_id: userId,
    fragrance_ref: fragranceRef,
    shared_by: payload.sharer_name,
    received_on: new Date().toISOString().slice(0, 10),
    channel: 'link',
    entity_kind: 'fragrance',
  })
  if (error) throw error
  return true
}

export function showShareHistorySurface({ shareLoopsEnabled, sharedRoutingEnabled, outgoing }) {
  return Boolean(shareLoopsEnabled && (!sharedRoutingEnabled || outgoing))
}
